import { Link } from "react-router-dom";
import { PublicNav } from "@/components/PublicNav";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <PublicNav />
      <div className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="text-3xl font-heading font-semibold tracking-tight mb-2">
          Privacy Policy
        </h1>
        <p className="text-muted-foreground mb-8">
          Last updated: March 13, 2026
        </p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Introduction</h2>
            <p className="text-muted-foreground">
              Distilled Intuition Inc. (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) operates the Distilled Intuition
              learning management platform at www.distilledintuition.training, including our web application
              and mobile apps. This Privacy Policy explains how we collect, use, disclose, and safeguard your
              information when you use our services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. Information We Collect</h2>
            <p className="text-muted-foreground mb-3">
              We collect information that you provide directly to us and information collected automatically
              when you use our services:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li><strong>Account information:</strong> Email address, name, and password when you register.</li>
              <li><strong>Profile information:</strong> Display name, organization, and preferences you choose to add.</li>
              <li><strong>Learning data:</strong> Course enrollments, progress, quiz scores, and skill tree completion.</li>
              <li><strong>Content you create:</strong> Courses, materials, and assessments you design as an instructor.</li>
              <li><strong>Communications:</strong> Messages sent through our platform or when you contact us.</li>
              <li><strong>Technical data:</strong> IP address, device type, browser, and usage logs.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. How We Use Your Information</h2>
            <p className="text-muted-foreground mb-3">We use your information to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Provide, maintain, and improve our services.</li>
              <li>Authenticate users and manage accounts.</li>
              <li>Deliver personalized learning experiences and track progress.</li>
              <li>Process payments and enforce subscription terms.</li>
              <li>Send transactional emails (e.g., password reset, enrollment confirmations).</li>
              <li>Respond to support requests and feedback.</li>
              <li>Analyze usage patterns to improve our platform.</li>
              <li>Comply with legal obligations and protect our rights.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. AI and Content Processing</h2>
            <p className="text-muted-foreground">
              Distilled Intuition uses AI to generate course outlines, assessments, and other content. When you
              use these features, we may process your course topics, materials, and learning objectives to
              produce AI-generated content. This processing occurs on our infrastructure and may involve
              third-party AI services. We do not use your content to train general-purpose AI models for
              purposes unrelated to providing our services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Data Storage and Security</h2>
            <p className="text-muted-foreground">
              We store your data on secure servers, including those operated by Supabase and other
              service providers. We use industry-standard encryption (TLS/SSL) for data in transit and
              appropriate safeguards for data at rest. Access to personal data is limited to authorized
              personnel and service providers who need it to operate our platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Data Sharing</h2>
            <p className="text-muted-foreground mb-3">
              We do not sell your personal data. We may share data with:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li><strong>Service providers:</strong> Hosting (e.g., Supabase), payment processors, email delivery.</li>
              <li><strong>Organizations:</strong> If you access the platform through an organization, we may share
                relevant data (e.g., progress, enrollments) with your organization&apos;s administrators.</li>
              <li><strong>Legal requirements:</strong> When required by law, court order, or to protect our rights.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Your Rights</h2>
            <p className="text-muted-foreground mb-3">
              Depending on your location, you may have the right to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1">
              <li>Access, correct, or delete your personal data.</li>
              <li>Object to or restrict certain processing.</li>
              <li>Data portability.</li>
              <li>Withdraw consent where processing is based on consent.</li>
              <li>Lodge a complaint with a supervisory authority.</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              To exercise these rights, contact us at the email below. You can also update your profile and
              account settings within the platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Cookies and Similar Technologies</h2>
            <p className="text-muted-foreground">
              We use cookies and local storage for session management, preferences, and analytics. You can
              control cookies through your browser settings. Disabling certain cookies may affect
              functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Children</h2>
            <p className="text-muted-foreground">
              Our services are intended for business and educational use. We do not knowingly collect
              personal information from children under 16. If you believe we have collected such information,
              please contact us and we will delete it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. International Transfers</h2>
            <p className="text-muted-foreground">
              Your data may be transferred to and processed in countries other than your residence. We
              ensure appropriate safeguards (e.g., standard contractual clauses) when transferring data
              outside the European Economic Area.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Changes</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. We will notify you of material changes
              by posting the updated policy on this page and updating the &quot;Last updated&quot; date. We encourage
              you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">12. Contact Us</h2>
            <p className="text-muted-foreground">
              For questions about this Privacy Policy or our data practices, contact us at:{" "}
              <a href="mailto:privacy@distilledintuition.training" className="text-primary hover:underline">
                privacy@distilledintuition.training
              </a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t">
          <Link to="/" className="text-primary hover:underline">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
